import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import useAppDispatch from "hooks/useAppDispatch";
import {
  forgotComplete,
  forgotStart,
} from "../../../../redux/features/user/userSlice";
import { isEmail, isValidPassword } from "utils/validation";

export default function useForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { code } = useParams();
  const dispatch = useAppDispatch();

  const apiKey = useSelector((state) => state.user.api_key);
  const forgotCompleteError = useSelector(
    (state) => state.user.forgotCompleteError
  );

  const [isCodeSent, setIsCodeSent] = useState(false);
  const [email, setEmail] = useState("");
  const [errorEmail, setErrorEmail] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [errorNewPassword, setErrorNewPassword] = useState(false);
  const [newPasswordRepeat, setNewPasswordRepeat] = useState("");
  const [errorNewPasswordRepeat, setErrorNewPasswordRepeat] = useState(false);
  const [isPasswordChanged, setIsPasswordChanged] = useState(false);
  const [serverError, setServerError] = useState(false);

  useEffect(() => {
    if (apiKey) {
      navigate("/");
    }
  }, [apiKey, navigate]);

  const { title, description } = useMemo(() => {
    if (code) {
      return {
        title: "Create password",
        description:
          "Strength requirements: min 8 characters, use symbols, numbers, letters, and special characters",
      };
    }
    if (isCodeSent) {
      return {
        title: "We sent you a link to reset your password",
        description: `The letter was sent to ${email}.`,
      };
    }
    return {
      title: "Forgot password?",
      description:
        "Enter your email associated with your account and we will send an email with instruction to reset your password",
    };
  }, [code, isCodeSent, email]);

  const doResendCode = useCallback((e) => {
    if (e) {
      e.preventDefault();
    }
    setEmail("");
    setIsCodeSent(false);
  }, []);

  const doForgotStart = useCallback(() => {
    if (!email) {
      setErrorEmail(t("forgotPassword.enterEmail"));
      return;
    }

    dispatch(forgotStart({ email }))
      .then(() => {
        setIsCodeSent(true);
      })
      .catch((err) => {
        setServerError(err?.error);
      });
  }, [dispatch, email, t]);

  const doForgotComplete = useCallback(() => {
    if (newPassword && newPasswordRepeat && newPassword === newPasswordRepeat) {
      dispatch(
        forgotComplete({
          code,
          password: newPassword,
        })
      )
        .then(() => {
          setIsPasswordChanged(true);
          setTimeout(() => {
            navigate("/login");
          }, 1500);
        })
        .catch(() => {});
    } else if (newPassword !== newPasswordRepeat) {
      setErrorNewPasswordRepeat(t("forgotPassword.passNotMatch"));
    }
  }, [dispatch, code, newPassword, newPasswordRepeat, navigate, t]);

  const validateEmail = useCallback(
    (value) => {
      if (value.length > 0 && !isEmail(value)) {
        setErrorEmail(t("forgotPassword.invalidEmail"));
      } else {
        setErrorEmail(false);
        setServerError(false);
      }
    },
    [t]
  );

  const validatePassword = useCallback(
    (value) => {
      if (value.length > 0 && value.length < 7) {
        setErrorNewPassword(t("forgotPassword.passLeast"));
      } else if (value.length === 0) {
        setErrorNewPassword(t("forgotPassword.enterPass"));
      } else if (!isValidPassword(value)) {
        setErrorNewPassword("Use the Latin alphabet and special characters!");
      } else {
        setErrorNewPassword(false);
      }
    },
    [t]
  );

  const validatePasswordRepeat = useCallback(
    (value) => {
      if (value.length === 0) {
        setErrorNewPasswordRepeat(t("forgotPassword.repeatPass"));
      } else if (value !== newPassword) {
        setErrorNewPasswordRepeat(t("forgotPassword.passNotMatch"));
      }
    },
    [newPassword, t]
  );

  return {
    t,
    code,
    title,
    description,
    email,
    errorEmail,
    newPassword,
    errorNewPassword,
    newPasswordRepeat,
    errorNewPasswordRepeat,
    serverError,
    isCodeSent,
    isPasswordChanged,
    forgotCompleteError,
    setEmail,
    setErrorEmail,
    setNewPassword,
    setErrorNewPassword,
    setNewPasswordRepeat,
    setErrorNewPasswordRepeat,
    doResendCode,
    doForgotStart,
    doForgotComplete,
    validateEmail,
    validatePassword,
    validatePasswordRepeat,
  };
}
